import { useRef, useState } from "react";
import { ATTACHMENT_LIMITS, type AttachmentInfo, type OrderItemResults } from "@probetalab/contracts";
import { getBlob, run } from "../lib/api.js";
import { attachmentById, attachmentSummary, prepareAttachment, rememberAttachment } from "../lib/attachments.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { ATTACH_KEY, attachmentIds, type Draft, LAB_KEY } from "./CaptureRows.js";
import { Icon } from "./icons.js";
import { Button, Chip } from "./ui.js";

/**
 * The study's attached documents in Captura (pilot request 2026-11-03 11:15, ADR 0030): «Adjuntar
 * PDF o foto» next to the informe, for the biopsy an outside pathology laboratory reports in its
 * own PDF. The file uploads at once (a staged artifact of the study) and joins the draft like any
 * typed value: «Sin guardar» until the study is saved, part of what validation signs, and after
 * validation it changes only through «Corregir resultado». The typed informe and the attachments
 * coexist; the report prints our page and then the attached pages, as they came.
 */
export function StudyAttachments({
  r,
  draft,
  locked,
  onChange,
  collapsible,
}: {
  r: OrderItemResults;
  draft: Draft;
  locked: boolean;
  onChange: (key: string, value: string) => void;
  /** Studies without a document row show one quiet button until someone attaches something. */
  collapsible: boolean;
}) {
  const toast = useToast();
  const pdfInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const ids = attachmentIds(draft);
  const savedIds = new Set((r.attachments ?? []).map((a) => a.id));
  const lab = draft[LAB_KEY] ?? "";
  const labDirty = lab.trim() !== (r.performed_by_lab ?? "");
  const removed = (r.attachments ?? []).filter((a) => !ids.includes(a.id));
  const full = ids.length >= ATTACHMENT_LIMITS.maxFiles;

  if (locked && ids.length === 0 && !lab) return null;
  if (collapsible && !locked && ids.length === 0 && !lab && !opened && removed.length === 0) {
    return (
      <div className="study-note collapsed">
        <Button size="sm" variant="ghost" icon="doc" onClick={() => setOpened(true)}>
          Adjuntar documento
        </Button>
      </div>
    );
  }

  async function attach(files: FileList | null) {
    if (!files?.length) return;
    let next = [...ids];
    for (const file of Array.from(files)) {
      if (next.length >= ATTACHMENT_LIMITS.maxFiles) {
        toast.error(`Se pueden adjuntar hasta ${ATTACHMENT_LIMITS.maxFiles} archivos por estudio.`);
        break;
      }
      setUploading(file.name || "archivo");
      try {
        const prepared = await prepareAttachment(file);
        if ("problem" in prepared) {
          toast.error(`${file.name}: ${prepared.problem}`);
          continue;
        }
        const info = await run<"results.attachment.upload", AttachmentInfo>("results.attachment.upload", { order_item_id: r.order_item_id, ...prepared });
        rememberAttachment(info);
        next = [...next, info.id];
        onChange(ATTACH_KEY, next.join(","));
      } catch (e) {
        toast.error(`${file.name}: ${errorMessage(e)}`);
      } finally {
        setUploading(null);
      }
    }
  }

  async function view(a: AttachmentInfo) {
    // The tab opens in the click, so a popup blocker lets it through.
    const tab = window.open("", "_blank");
    try {
      const blob = await getBlob("resultAttachmentFile", { params: { id: a.id } }, a.content_type);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
    } catch (e) {
      tab?.close();
      toast.error(errorMessage(e));
    }
  }

  const remove = (id: string) => onChange(ATTACH_KEY, ids.filter((x) => x !== id).join(","));
  const dirty = ids.join(",") !== (r.attachments ?? []).map((a) => a.id).join(",") || labDirty;

  return (
    <section className="attach-block" aria-label={`Documentos adjuntos de ${r.study_name}`}>
      <div className="attach-head">
        <span className="label">Documentos adjuntos</span>
        <span className="muted small">{locked ? "Se imprimen después del estudio, tal como vienen." : "PDF o foto del informe. Sus páginas se agregan al reporte después de este estudio, tal como vienen."}</span>
        {dirty && (
          <Chip tone="amber" dot>
            Sin guardar
          </Chip>
        )}
      </div>
      {ids.length > 0 && (
        <ul className="attach-list">
          {ids.map((id) => {
            const a = attachmentById(id) ?? (r.attachments ?? []).find((x) => x.id === id);
            if (!a) return null;
            return (
              <li key={id} className="attach-item">
                <Icon name="doc" />
                <span className="attach-name">
                  <span className="attach-file">{a.file_name}</span>
                  <span className="muted small">{attachmentSummary(a)}</span>
                </span>
                {!savedIds.has(id) && (
                  <Chip tone="amber" dot>
                    Nuevo
                  </Chip>
                )}
                <Button size="sm" variant="ghost" icon="eye" onClick={() => void view(a)}>
                  Ver
                </Button>
                {!locked && (
                  <Button size="sm" variant="ghost" icon="x" onClick={() => remove(id)} aria-label={`Quitar ${a.file_name}`}>
                    Quitar
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!locked && removed.length > 0 && <p className="muted small">Al guardar se quita: {removed.map((a) => a.file_name).join(", ")}.</p>}
      {!locked && (
        <div className="row wrap attach-actions">
          <Button size="sm" variant="secondary" icon="plus" busy={uploading !== null} disabled={uploading !== null || full} onClick={() => pdfInput.current?.click()}>
            Adjuntar PDF o foto
          </Button>
          <Button size="sm" variant="ghost" icon="plus" disabled={uploading !== null || full} onClick={() => cameraInput.current?.click()}>
            Tomar foto
          </Button>
          {uploading && <span className="muted small">Subiendo {uploading}…</span>}
          <input
            ref={pdfInput}
            type="file"
            hidden
            multiple
            accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
            aria-label="Adjuntar PDF o foto"
            onChange={(e) => {
              void attach(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={cameraInput}
            type="file"
            hidden
            accept="image/*"
            capture="environment"
            aria-label="Tomar foto del informe"
            onChange={(e) => {
              void attach(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      )}
      {(ids.length > 0 || lab) && (
        <label className="field attach-lab">
          <span className="label">Laboratorio que realizó el estudio</span>
          <input
            className="input"
            aria-label="Laboratorio que realizó el estudio"
            value={lab}
            disabled={locked}
            maxLength={ATTACHMENT_LIMITS.maxLabNameLength}
            placeholder="Opcional: si lo procesó otro laboratorio"
            {...NO_AUTOFILL}
            onChange={(e) => onChange(LAB_KEY, e.target.value)}
          />
          {!locked && <span className="muted small">Si lo escribes, el reporte dice que el estudio lo realizó ese laboratorio de referencia.</span>}
        </label>
      )}
    </section>
  );
}
